import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Typography,
  Modal,
  FormControl,
  TextField,
  MenuItem,
  InputLabel,
  Select
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormik, Form, FormikProvider } from 'formik';
import { SnackBarAlert } from '../../components/HelperComponents';
import { useAuth } from '../../components/customContextProviders/AuthContext';
import { GetAllMerchants } from '../../network/Merchants';
import { CreateTicket, GetTicketByFreshdeskId, UpdateTicket } from '../network/Ticket';
import {
  ADD_TICKET_COMPLAINT_TYPE_OPTIONS,
  addTicketModalBoxSx
} from '../utils/ticketManagementConstants';
import {
  addTicketFormValidationSchema,
  buildCreateTicketFormData,
  buildUpdateTicketFormData,
  cleanTicketDescriptionText,
  normalizeTicketStatusForApi,
  parseCreateOrUpdateTicketResponse,
  pickTicketFromFreshdeskTicketsResponse
} from '../utils/ticketManagementUtils';

export default function AddTicketModal({
  open,
  handleClose,
  ticketData,
  setTicketData,
  merchantData,
  merchantDetails,
  onReload
}) {
  const { userLoginOrg } = useAuth();
  const [isSnackBarOpen, setIsSnackBarOpen] = useState(false);
  const [snackMeta, setSnackMeta] = useState({ title: '', severity: 'success' });
  const [fetchedMerchants, setFetchedMerchants] = useState([]);
  const [updateTicketDetailFromFreshdesk, setUpdateTicketDetailFromFreshdesk] = useState(null);
  const freshdeskFetchForTicketIdRef = useRef(null);

  const ticketRowForForm =
    ticketData?.ticket && updateTicketDetailFromFreshdesk
      ? {
        ...ticketData.ticket,
        ...updateTicketDetailFromFreshdesk,
        status: ticketData.ticket.status
      }
      : ticketData?.ticket;

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      subject: ticketRowForForm?.subject || '',
      description:
        cleanTicketDescriptionText(
          ticketRowForForm?.description_text || ticketRowForForm?.description
        ) || '',
      email: ticketRowForForm?.email || merchantData?.email || '',
      phone: ticketRowForForm?.phone || merchantData?.phone || merchantDetails?.phone || '',
      vpa:
        ticketRowForForm?.custom_fields?.cf_vpa ||
        ticketRowForForm?.merchant?.vpa ||
        ticketRowForForm?.vpa ||
        merchantDetails?.vpa ||
        '',
      complaintType:
        ticketRowForForm?.custom_fields?.cf_complaint_type || ticketRowForForm?.ticketType || '',
      status: normalizeTicketStatusForApi(ticketRowForForm?.status),
      comment: '',
      isUpdate: !!ticketData?.ticket?.id,
      client: userLoginOrg || '',
      merchantId: merchantDetails?.id || ticketRowForForm?.merchantId || '',
      name: ticketRowForForm?.custom_fields?.cf_name || merchantDetails?.name || '',
      accountNumber:
        ticketRowForForm?.custom_fields?.cf_account_number ||
        merchantDetails?.details?.uniqueRef ||
        '',
      attachments: []

    },

    validationSchema: addTicketFormValidationSchema,

    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        if (ticketData?.ticket?.id) {
          await UpdateTicket(
            ticketData?.ticket?.id,
            buildUpdateTicketFormData({
              status: normalizeTicketStatusForApi(values.status),
              comment: values.comment
            })
          );

          setSnackMeta({
            title: `${ticketData?.ticket?.freshdeskTicketId ?? ticketData?.ticket?.id
              } — ticket updated successfully`,
            severity: 'success'
          });
        } else {
          const response = await CreateTicket(buildCreateTicketFormData(values));
          const created = parseCreateOrUpdateTicketResponse(response);
          setSnackMeta({
            title: `${created?.freshdeskTicketId ?? 'Ticket'} — created successfully`,
            severity: 'success'
          });
        }
      } catch (error) {
        const errBody = error?.response?.data;
        const msg =
          (typeof errBody?.errors?.status === 'string' && errBody.errors.status) ||
          errBody?.message ||
          'Failed to save ticket';
        setSnackMeta({
          title: msg,
          severity: 'error'
        });
        setIsSnackBarOpen(true);
        setSubmitting(false);
        return;
      }

      setSubmitting(false);
      setIsSnackBarOpen(true);
      handleModalCloseInternal();
      onReload();
    }
  });

  const { values, setFieldValue, errors, touched, isSubmitting, handleSubmit, getFieldProps } =
    formik;

  useEffect(() => {
    if (!open) {
      setUpdateTicketDetailFromFreshdesk(null);
      return;
    }

    const row = ticketData?.ticket;
    if (!row?.id) {
      setUpdateTicketDetailFromFreshdesk(null);
      return;
    }

    const freshdeskDisplayId = row.freshdeskTicketId ?? row.freshDeskId;
    const hasFreshdeskId =
      freshdeskDisplayId != null &&
      freshdeskDisplayId !== '' &&
      String(freshdeskDisplayId).trim() !== '' &&
      String(freshdeskDisplayId) !== '---';

    if (!hasFreshdeskId) {
      setUpdateTicketDetailFromFreshdesk(null);
      return;
    }

    const internalTicketId = row.id;
    freshdeskFetchForTicketIdRef.current = internalTicketId;
    setUpdateTicketDetailFromFreshdesk(null);

    let cancelled = false;
    GetTicketByFreshdeskId(freshdeskDisplayId)
      .then((response) => {
        if (cancelled) return;
        if (freshdeskFetchForTicketIdRef.current !== internalTicketId) return;
        const picked = pickTicketFromFreshdeskTicketsResponse(response);
        if (picked && typeof picked === 'object' && Object.keys(picked).length > 0) {
          setUpdateTicketDetailFromFreshdesk(picked);
        } else {
          setUpdateTicketDetailFromFreshdesk(null);
        }
      })
      .catch(() => {
        if (!cancelled && freshdeskFetchForTicketIdRef.current === internalTicketId) {
          setUpdateTicketDetailFromFreshdesk(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    ticketData?.ticket?.id,
    ticketData?.ticket?.freshdeskTicketId,
    ticketData?.ticket?.freshDeskId
  ]);

  const fetchMerchants = () => {
    GetAllMerchants(1, 100, { phone: values.phone })
      .then((res) => {
        const merchants = res?.data?.data || [];
        setFetchedMerchants(merchants);
        if (merchants.length === 1) {
          const merchant = merchants[0];
          setFieldValue('merchantId', merchant.id || '');
          setFieldValue('vpa', merchant.vpa || '');
          setFieldValue('name', merchant.name || '');
          setFieldValue('email', merchant.email || '');
          setFieldValue('accountNumber', merchant.details?.uniqueRef || '');
        } else if (merchants.length === 0) {
          setFieldValue('merchantId', '');
          setFieldValue('vpa', '');
          setFieldValue('name', '');
          setFieldValue('email', '');
          setFieldValue('accountNumber', '');
        }
      })
      .catch((err) => {
        console.log(err);
        setFetchedMerchants([]);
      });
  };

  const handleModalCloseInternal = () => {
    handleClose();
    formik.resetForm();
    setFetchedMerchants([]);
    setUpdateTicketDetailFromFreshdesk(null);
    if (ticketData?.ticket?.id) {
      setTicketData(null);
    }
  };

  useEffect(() => {
    if (!ticketData?.ticket?.id && values.phone && values.phone.length === 10) {
      if (!merchantDetails) {
        fetchMerchants();
      }
    } else if (!ticketData?.ticket?.id && (!values.phone || values.phone.length < 10)) {
      setFetchedMerchants([]);
      if (!merchantDetails) {
        setFieldValue('merchantId', '');
        setFieldValue('vpa', '');
        setFieldValue('name', '');
        setFieldValue('email', '');
        setFieldValue('accountNumber', '');
      }
    }
  }, [values.phone, ticketData?.ticket?.id, setFieldValue, merchantDetails]);

  const isCreateMode = !ticketData?.ticket?.id;
  const isFormDisabled = isCreateMode && (!values.phone || values.phone.length < 10);

  return (
    <Box>
      <Modal open={open} onClose={handleModalCloseInternal}>
        <FormikProvider value={formik}>
          <Form noValidate onSubmit={handleSubmit}>
            <Box sx={addTicketModalBoxSx}>
              <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat' }}>
                {ticketData?.ticket?.id ? 'Update Ticket' : 'Create Ticket'}
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                {!ticketData?.ticket?.id && (
                  <Grid size={{ xs: 6 }}>
                    <FormControl fullWidth>
                      <TextField
                        label="Contact Number"
                        {...getFieldProps('phone')}
                        error={Boolean(touched.phone && errors.phone)}
                        helperText={touched.phone && errors.phone}
                      />
                    </FormControl>
                  </Grid>
                )}

                <Grid size={{ xs: ticketData?.ticket?.id ? 12 : 6 }}>
                  <FormControl fullWidth>
                    {!ticketData?.ticket?.id && fetchedMerchants.length > 1 ? (
                      <>
                        <InputLabel>VPA</InputLabel>
                        <Select
                          label="VPA"
                          {...getFieldProps('vpa')}
                          onChange={(event) => {
                            const selectedVpa = event.target.value;
                            setFieldValue('vpa', selectedVpa);
                            const merchant = fetchedMerchants.find(
                              (item) => item.vpa === selectedVpa
                            );
                            if (merchant) {
                              setFieldValue('merchantId', merchant.id || '');
                              setFieldValue('name', merchant.name || merchant.merchantName || '');
                              setFieldValue('email', merchant.email || '');
                              setFieldValue(
                                'accountNumber',
                                merchant.details?.uniqueRef || merchant.accountNumber || ''
                              );
                            }
                          }}
                          error={Boolean(touched.vpa && errors.vpa)}
                        >
                          {fetchedMerchants.map((merchant) => (
                            <MenuItem key={merchant.vpa} value={merchant.vpa}>
                              {merchant.vpa}
                            </MenuItem>
                          ))}
                        </Select>
                      </>
                    ) : (
                      <TextField
                        label="VPA"
                        {...getFieldProps('vpa')}
                        error={Boolean(touched.vpa && errors.vpa)}
                        helperText={touched.vpa && errors.vpa}
                        disabled={isFormDisabled}
                      />
                    )}
                  </FormControl>
                </Grid>

                {!ticketData?.ticket?.id && (
                  <>
                    <Grid size={{ xs: 6 }}>
                      <FormControl fullWidth>
                        <TextField
                          label="Name"
                          {...getFieldProps('name')}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                          disabled={isFormDisabled || !values.vpa}
                        />
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <FormControl fullWidth>
                        <TextField
                          label="Contact Email"
                          {...getFieldProps('email')}
                          error={Boolean(touched.email && errors.email)}
                          helperText={touched.email && errors.email}
                          disabled={isFormDisabled || !values.vpa}
                        />
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 6 }}>
                      <FormControl fullWidth>
                        <TextField
                          label="Account Number"
                          {...getFieldProps('accountNumber')}
                          error={Boolean(touched.accountNumber && errors.accountNumber)}
                          helperText={touched.accountNumber && errors.accountNumber}
                          disabled={isFormDisabled || !values.vpa}
                        />
                      </FormControl>
                    </Grid>
                  </>
                )}

                {ticketData?.ticket?.id && (
                  <Grid size={{ xs: 6 }}>
                    <FormControl fullWidth>
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        {...getFieldProps('status')}
                        error={Boolean(touched.status && errors.status)}
                      >
                        <MenuItem value="Open">Open</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Resolved">Resolved</MenuItem>
                        <MenuItem value="Closed">Closed</MenuItem>
                        <MenuItem value="Waiting on customer">Waiting on customer</MenuItem>
                        <MenuItem value="Waiting on third party">Waiting on third party</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Complaint Type</InputLabel>
                    <Select
                      label="Complaint Type"
                      {...getFieldProps('complaintType')}
                      error={Boolean(touched.complaintType && errors.complaintType)}
                      disabled={isFormDisabled}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 180,
                            overflowY: 'auto'
                          }
                        }
                      }}
                    >
                      {ADD_TICKET_COMPLAINT_TYPE_OPTIONS.map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth>
                    <TextField
                      label="Subject"
                      {...getFieldProps('subject')}
                      error={Boolean(touched.subject && errors.subject)}
                      helperText={touched.subject && errors.subject}
                      disabled={isFormDisabled}
                    />
                  </FormControl>
                </Grid>
                {!ticketData?.ticket?.id && (
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <TextField
                        label="Description"
                        multiline
                        rows={3}
                        {...getFieldProps('description')}
                        error={Boolean(touched.description && errors.description)}
                        helperText={touched.description && errors.description}
                        disabled={isFormDisabled}
                      />
                    </FormControl>
                  </Grid>
                )}
                {!ticketData?.ticket?.id && (
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: isFormDisabled ? 'action.disabled' : 'rgba(0,0,0,0.23)',
                          borderRadius: 1,
                          p: 1.5,
                          '&:hover': { borderColor: isFormDisabled ? 'action.disabled' : 'text.primary' }
                        }}
                      >
                        <Typography variant="caption" color={isFormDisabled ? 'text.disabled' : 'text.secondary'} display="block" sx={{ mb: 1 }}>
                          Attachments (images or PDF)
                        </Typography>
                        <input
                          type="file"
                          accept="image/*,.pdf"
                          multiple
                          disabled={isFormDisabled}
                          onChange={(e) => {
                            const files = Array.from(e.target.files || []).slice(0, 3);
                            setFieldValue('attachments', files);
                          }}
                          style={{ display: 'block', width: '100%' }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }} display="block">
                          Max 3 files (images or PDF)
                        </Typography>
                        {values.attachments?.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            {values.attachments.map((file, idx) => (
                              <Typography key={idx} variant="caption" display="block" color="text.secondary">
                                {file.name} ({(file.size / 1024).toFixed(1)} KB)
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    </FormControl>
                  </Grid>
                )}
                {ticketData?.ticket?.id && (
                  <Grid size={{ xs: 12 }}>
                    <FormControl fullWidth>
                      <TextField
                        label="Add Internal Comment"
                        multiline
                        rows={3}
                        {...getFieldProps('comment')}
                        error={Boolean(touched.comment && errors.comment)}
                        helperText={touched.comment && errors.comment}
                      />
                    </FormControl>
                  </Grid>
                )}
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LoadingButton
                  loading={isSubmitting}
                  sx={{ mt: 2 }}
                  variant="contained"
                  type="submit"
                >
                  {ticketData?.ticket?.id ? 'Update Ticket' : 'Create Ticket'}
                </LoadingButton>
              </Box>
            </Box>
          </Form>
        </FormikProvider>
      </Modal>

      <SnackBarAlert
        open={isSnackBarOpen}
        title={snackMeta.title}
        severity={snackMeta.severity}
        handleClose={() => setIsSnackBarOpen(false)}
      />
    </Box>
  );
}

AddTicketModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  onCreated: PropTypes.func
};
